/**
 *
 * ThumbnailUpload
 *
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

// styles
import "./style.scss";

// layout components
import ErrorBoundary from "engine/ErrorBoundary";
import Preloader from "components/Preloader";

// antd component
import { Upload, message, Col, Form, Image } from "antd";

// icons
import CustomIcons from "components/CustomIcons";

// utils
import api from "utils/api";

// Helper
import {
  beforeUploadCheckSize,
  resizeFile,
  scrubFileName,
} from "utils/uploadHelper";

function ThumbnailUpload({
  name,
  label,
  method,
  mode,
  size,
  description,
  wrpaCol,
  col,
  wrapForm,
  wrapperClassName,
  required,
  value,
  onlyRead,
  visibility,
  // actions
  onResult,
  onRemove,
  // default props
  className,
  ...rest
}) {
  const childClassNames = classNames(
    "thumbnail-upload-wrapper",
    size,
    className,
  );
  const { t, i18n } = useTranslation();

  const key = "new";
  const [loading, setLoading] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState("");

  useEffect(() => {
    if (method != key || !!onlyRead) {
      !loading && setImageUrl(value);
    } else if (method == mode == key && !onlyRead) {
      setImageUrl("");
      setRemoveLoading(false);
      setLoading(false);
    }
    return () => {
      setImageUrl("");
      setRemoveLoading(false);
      setLoading(false);
    };
  }, []);

  useEffect(() => {
    if (method != key || !!onlyRead) {
      !loading && setImageUrl(value);
    } else if (method == mode == key && !onlyRead) {
      setImageUrl("");
      setRemoveLoading(false);
      setLoading(false);
    }
  }, [value, method, mode]);

  const handleUpload = async (file) => {
    const tempFile = beforeUploadCheckSize(file);

    if (!!!tempFile) return false;

    setLoading(true);
    const formPayload = new FormData();
    //  formData.append('file', file);
    try {
      // compress uploaded file
      resizeFile(file)
        // need to wait the image are compress
        .then(async (compressFile) => {
          // change Blob to File
          let tempCompressFile = new File(
            [compressFile],
            scrubFileName(compressFile.name),
          );

          // add uploading file to array of all files
          tempCompressFile.status = "uploading";

          formPayload.append("file", tempCompressFile);

          if (!loading) {
            const response = await api.media.upload(formPayload);
            setImageUrl(response?.url);
            response?.url && !!onResult && onResult(response?.url);
            setLoading(false);
          } else {
            setImageUrl("");
            setLoading(false);
          }
        })
        .catch((err) => {
          setImageUrl("");
          setLoading(false);
          console.error({ err });
          message.error(t("errors.somethingWrongTryAgain"), 2);
        });
    } catch (error) {
      message.error(t("errors.somethingWrongTryAgain"), 2);
      console.error({ error });
      setLoading(false);
    }
  };

  const handleRemove = useCallback(() => {
    setRemoveLoading(true);

    if (!!imageUrl && !removeLoading) {
      api.media
        .delete(imageUrl)
        .then((response) => {
          !!onRemove &&
            onRemove({
              response,
              error: null,
              status: 200,
            });
          setRemoveLoading(false);
          setImageUrl("");
        })
        .catch((error) => {
          console.error(error);
          const notExists =
            error?.response?.data?.message?.indexOf("exist") >= 0;
          if (error?.response?.data?.code == 422 || !!notExists) {
            !!onRemove &&
              onRemove({
                response: null,
                error: error?.response?.data,
                status: !!notExists ? 200 : error?.response?.status,
              });
            setRemoveLoading(false);
            setImageUrl("");
          }
        });
    } else {
      !!onRemove &&
        onRemove({
          response: null,
          error: {
            message: t("inputs.fileToRemove"),
          },
          status: 400,
        });
      setRemoveLoading(false);
      setImageUrl("");
    }
  }, [imageUrl, removeLoading, onRemove]);

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file.originFileObj);
        reader.onload = () => resolve(reader.result);
      });
    }
    setImageUrl(file.url || file.preview);
  };
  const ColWrapper = ({ children }) =>
    wrpaCol ? (
      <Col
        key={name + "-col"}
        md={col || 24}
        xs={24}
        offset={null}
        className={`thumbnail-upload-column ${name}`}
      >
        {children}
      </Col>
    ) : (
      <>{children}</>
    );

  const FormItemWrapper = ({ children }) =>
    wrapForm ? (
      <Form.Item
        key={name + "-form-item"}
        name={name}
        label={false}
        className={`thumbnail-upload-form-item ${name}`}
        initialValue={(!!imageUrl && imageUrl) || value}
        rules={[
          {
            type: "string",
            min: 2,
            required: required || false,
            message: t("inputs.mandatoryField"),
            validateTrigger: "onSubmit",
          },
        ]}
      >
        {children}
      </Form.Item>
    ) : (
      <>
        {!!label && label}
        {children}
      </>
    );

  const uploadOption = {
    multiple: false,
    maxCount: 1,
    listType: "picture-card",
    className: "avatar-uploader",
    showUploadList: false,
    beforeUpload: handleUpload,
    onPreview: handlePreview,
  };

  !wrapForm && (uploadOption.name = name);

  const PreviewImage = useMemo(() => {
    return (
      <div className="thumbnail-upload-preview__wrapper">
        {removeLoading && <Preloader size="small" />}
        <Image
          rootClassName="thumbnail-upload-preview"
          src={imageUrl}
          preview={{
            mask: (
              <div
                style={{ color: "white", fontSize: "1rem", fontWeight: "bold" }}
              >
                {t("common.overview")}
              </div>
            ),
          }}
        />
        {!onlyRead && !removeLoading && (
          <CustomIcons
            className="thumbnail-upload-delete"
            type="garbage"
            onClick={handleRemove}
          />
        )}
      </div>
    );
  }, [imageUrl, onlyRead, removeLoading, i18n?.language]);

  const UploadImage = useMemo(() => {
    return (
      <ColWrapper>
        <FormItemWrapper>
          {!!imageUrl && PreviewImage}
          {!imageUrl && (
            <Upload {...uploadOption}>
              <div className="thumbnail-upload-card">
                {loading ? (
                  <Preloader size="small" />
                ) : (
                  <CustomIcons type="thumbnail" />
                )}
                <div style={{ marginTop: 9 }}>
                  {(!!description && description) || t("inputs.addImageToPage")}
                </div>
              </div>
            </Upload>
          )}
        </FormItemWrapper>
      </ColWrapper>
    );
  }, [imageUrl, loading, description, i18n?.language]);

  return !!visibility && (
    <>
      {!!wrapperClassName && (
        <div className={wrapperClassName}>
          <div className={childClassNames}>
            <ErrorBoundary>
              {!!label && <span className="thumbnail-label">{label}</span>}
              {!!onlyRead && !!imageUrl && PreviewImage}
              {!onlyRead && UploadImage}
            </ErrorBoundary>
          </div>
        </div>
      )}
      {!!!wrapperClassName && (
        <div className={childClassNames}>
          <ErrorBoundary>
            {!!label && <span className="thumbnail-label">{label}</span>}
            {!!onlyRead && !!imageUrl && PreviewImage}
            {!onlyRead && UploadImage}
          </ErrorBoundary>
        </div>
      )}
    </>
  );
}

ThumbnailUpload.defaultProps = {
  name: "thumbnail",
  size: "full",
  method: "new",
  mode: "new",
  visibility: true,
  wrpaCol: false,
  wrapperClassName: false,
  label: false,
  col: 24,
  onlyRead: false,
  wrapForm: false,
  required: false,
  value: null
};

export default ThumbnailUpload;
